import React from 'react';
import { FolderOpen, Play, Settings } from 'lucide-react';
import { BuildSettings as BuildSettingsType } from '../../../../core/types/build.types';

interface Props {
  settings: BuildSettingsType;
  scenes: string[];
  onUpdate: (settings: Partial<BuildSettingsType>) => void;
  onBrowseOutput: () => void;
}

export const BuildSettings: React.FC<Props> = ({
  settings,
  scenes,
  onUpdate,
  onBrowseOutput,
}) => {
  return (
    <div className="space-y-4">
      {/* Starting Scene */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1">
          Starting Scene
        </label>
        <select
          value={settings.startingScene}
          onChange={(e) => onUpdate({ startingScene: e.target.value })}
          className="w-full px-3 py-2 text-sm bg-zinc-900 border border-zinc-600 rounded-lg
            focus:border-blue-500 focus:outline-none text-zinc-200"
        >
          <option value="">Select a scene...</option>
          {scenes.map((scene) => (
            <option key={scene} value={scene}>{scene}</option>
          ))}
        </select>
      </div>

      {/* Output Directory */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1">
          Output Directory
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={settings.outputDirectory}
            onChange={(e) => onUpdate({ outputDirectory: e.target.value })}
            className="flex-1 px-3 py-2 text-sm bg-zinc-900 border border-zinc-600 rounded-lg
              focus:border-blue-500 focus:outline-none text-zinc-200"
          />
          <button
            onClick={onBrowseOutput}
            className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg"
          >
            <FolderOpen size={16} />
          </button>
        </div>
      </div>

      {/* Build Configuration */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1">
          Configuration
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => onUpdate({ configuration: 'development' })}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm
              ${settings.configuration === 'development'
                ? 'bg-yellow-600/20 border-2 border-yellow-500 text-yellow-300'
                : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700'
              }`}
          >
            <Settings size={14} />
            Development
          </button>
          <button
            onClick={() => onUpdate({ configuration: 'production' })}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm
              ${settings.configuration === 'production'
                ? 'bg-blue-600/20 border-2 border-blue-500 text-blue-300'
                : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700'
              }`}
          >
            <Play size={14} />
            Production
          </button>
        </div>
      </div>

      {/* Target Resolution */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1">
          Target Resolution
        </label>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            value={settings.targetResolution.width}
            onChange={(e) => onUpdate({
              targetResolution: { ...settings.targetResolution, width: parseInt(e.target.value) || 720 }
            })}
            className="w-24 px-3 py-2 text-sm bg-zinc-900 border border-zinc-600 rounded-lg
              focus:border-blue-500 focus:outline-none text-zinc-200 text-center"
          />
          <span className="text-zinc-500">x</span>
          <input
            type="number"
            value={settings.targetResolution.height}
            onChange={(e) => onUpdate({
              targetResolution: { ...settings.targetResolution, height: parseInt(e.target.value) || 1560 }
            })}
            className="w-24 px-3 py-2 text-sm bg-zinc-900 border border-zinc-600 rounded-lg
              focus:border-blue-500 focus:outline-none text-zinc-200 text-center"
          />
          {/* Presets */}
          <select
            onChange={(e) => {
              const [w, h] = e.target.value.split('x').map(Number);
              if (w && h) onUpdate({ targetResolution: { width: w, height: h } });
            }}
            className="px-2 py-2 text-sm bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-300"
          >
            <option value="">Preset</option>
            <option value="720x1560">720x1560 (Mobile Portrait)</option>
            <option value="1920x1080">1920x1080 (FHD)</option>
            <option value="1280x720">1280x720 (HD)</option>
            <option value="2560x1440">2560x1440 (2K)</option>
            <option value="3840x2160">3840x2160 (4K)</option>
          </select>
        </div>
      </div>
    </div>
  );
};
